import PriceDisplay from '@/components/PriceDisplay';
import { useCheckoutBooking } from '@/hooks/useCheckoutBooking';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import type { ApiErrorResponse } from '@/types/api';
import type { BookedAncillaryDTO } from '@/types/dto/agent';
import { showErrorToast } from '@/utils/errorToast';
import { getPassengerName } from '@/utils/passenger';
import type { CheckoutValues } from '@/utils/zodSchema';
import { Button, Icons, Typography } from '@fleet/webshop-ui';
import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface AncillaryRowProps {
  ancillary: BookedAncillaryDTO;
  icon: ReactNode;
  handleRemoveAncillary: (ancillary: BookedAncillaryDTO) => Promise<void>;
}

const AncillaryRow: FC<AncillaryRowProps> = ({
  ancillary,
  icon,
  handleRemoveAncillary,
}) => {
  const [isPending, setIsPending] = useState(false);
  const booking = useCheckoutBooking();
  const passengerIdsByAncillaryId = useMemo(() => {
    const passengerIdsByAncillaryId: Record<
      NonNullable<BookedAncillaryDTO['id']>,
      Array<string>
    > = {};

    booking?.bookedTrips?.forEach(({ bookedOffers }) =>
      bookedOffers?.forEach(({ admissions }) =>
        admissions?.forEach(({ passengerIds, ancillaries }) =>
          ancillaries?.forEach(({ id }) => {
            if (!id) {
              return;
            }

            passengerIdsByAncillaryId[id] = [
              ...(passengerIdsByAncillaryId[id] || []),
              ...(passengerIds || []),
            ];
          })
        )
      )
    );

    return passengerIdsByAncillaryId;
  }, [booking?.bookedTrips]);
  const { watch } = useFormContext<CheckoutValues>();
  const passengers = watch('passengers');
  const passengerIds = ancillary.id
    ? passengerIdsByAncillaryId[ancillary.id]
    : [];
  // We are accessing the first element of passengerIds array because generally it contains only single ID per admission.
  // The only admission that could have more than one ID in the array is "compartment" which means that whole room in a vehicle is bought.
  // Since Arenaways wont be using "compartment", we will tackle this part with a separate task.
  const currentPassengerIndex = passengers.findIndex(
    ({ id }) => id === passengerIds[0]
  );

  const handleRemoveClick = useCallback(async () => {
    setIsPending(true);
    try {
      await handleRemoveAncillary(ancillary);
    } catch (e) {
      showErrorToast(
        <TransAlert i18nKey="ancillaryRemovalFailed" />,
        e as ApiErrorResponse
      );
    } finally {
      setIsPending(false);
    }
  }, [ancillary, handleRemoveAncillary]);

  return (
    <div className="grid grid-cols-[auto,1fr,auto]">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center gap-2 px-2">
        <Typography variant="body1-bold">
          {ancillary.productDescription}
        </Typography>
        <Typography variant="body2-bold" className="text-primary">
          {ancillary.price?.amount === 0 ? (
            <TransText i18nKey="free" />
          ) : (
            <PriceDisplay amount={ancillary.price?.amount} />
          )}
        </Typography>
      </div>
      <Button
        data-testid="ancillary-remove-button"
        variant="ghost"
        attention
        className="h-auto tablet:px-1 tablet:py-0.5"
        onClick={handleRemoveClick}
        isPending={isPending}
        startAdornment={<Icons.removeCircle height={16} width={16} />}
      >
        <TransText i18nKey="remove" />
      </Button>
      <div />
      <Typography variant="body2" className="px-2 text-neutral-light">
        {getPassengerName(
          passengers[currentPassengerIndex],
          currentPassengerIndex
        )}
      </Typography>
    </div>
  );
};

export default AncillaryRow;
